import { useEffect, useState } from "react";
import { cn } from "./ui";
import { PaneHead, Switch } from "./settings-parts";
import { notifyChannels, notifyDefaults, notifyDigests, notifyEvents, type NotifyPrefs } from "./notifications-meta";
import { PREFS_EVENT } from "./use-notifications";
import { api, apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Раздел «Уведомления» в настройках рабочего пространства.
   Куда присылать, о чём присылать, как часто и в какие часы молчать. Хранится на сервере у каждого пользователя. */

const TIME_INPUT =
  "h-8 w-[6.4rem] box-border rounded-btn border border-border bg-background px-2 text-[11.5px] tabular-nums text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

export default function SettingsNotifications({
  onFlash,
  onTest,
}: {
  onFlash: (text: string) => void;
  onTest: () => void;
}) {
  const [prefs, setPrefs] = useState<NotifyPrefs>(notifyDefaults);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<NotifyPrefs>("/notifications/prefs").then(
      (p) => {
        setPrefs(p);
        setEmail(p.email ?? "");
      },
      (err) => onFlash(apiErrorText(err)),
    );
  }, [onFlash]);

  const channels = notifyChannels.map((c) => ({ ...c, on: prefs.channels[c.id] }));
  const events = notifyEvents.map((e) => ({ ...e, on: prefs.events[e.id] }));
  const { digest, quiet: q } = prefs;
  const quiet = q.on;

  const onChannels = channels.filter((c) => c.on).length;
  const onEvents = events.filter((e) => e.on).length;

  const setQuiet = (patch: Partial<NotifyPrefs["quiet"]>) => setPrefs((p) => ({ ...p, quiet: { ...p.quiet, ...patch } }));

  const toggleChannel = (id: keyof NotifyPrefs["channels"]) => {
    const target = channels.find((c) => c.id === id);
    setPrefs((p) => ({ ...p, channels: { ...p.channels, [id]: !p.channels[id] } }));
    if (id !== "browser" || target?.on) return;
    // включаем браузерные — сразу спрашиваем разрешение у браузера, без него всплывашек не будет
    if (typeof Notification === "undefined") {
      onFlash(t("Браузер не поддерживает всплывающие уведомления"));
      return;
    }
    if (Notification.permission === "granted") {
      onFlash(t("Браузерные уведомления включены — не забудьте сохранить"));
      return;
    }
    Notification.requestPermission()
      .then((res) => onFlash(res === "granted" ? t("Браузерные уведомления включены — не забудьте сохранить") : t("Браузер не дал разрешение — включите его в настройках сайта")))
      .catch(() => onFlash(t("Браузер не дал разрешение — включите его в настройках сайта")));
  };

  const toggleEvent = (id: keyof NotifyPrefs["events"]) => {
    setPrefs((p) => ({ ...p, events: { ...p.events, [id]: !p.events[id] } }));
  };

  const save = async (next: NotifyPrefs, text: string) => {
    setBusy(true);
    try {
      const saved = await api<NotifyPrefs>("/notifications/prefs", { method: "PUT", json: { channels: next.channels, events: next.events, digest: next.digest, quiet: next.quiet } });
      setPrefs(saved);
      window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: saved }));
      onFlash(text);
    } catch (err) {
      onFlash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => void save(notifyDefaults, t("Настройки уведомлений вернули к значениям по умолчанию"));

  return (
    <div className="p-3.5 md:p-5">
      <PaneHead
        icon="ri-notification-3-line"
        title={t("Уведомления")}
        text={t("Выберите, куда и о чём присылать уведомления. Настройки применяются к вашему профилю, а не ко всему рабочему пространству.")}
      />

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {/* куда присылать */}
        <section className="rounded-btn border border-border bg-background/50 p-3.5">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-[12px] font-semibold">{t("Куда присылать")}</p>
            <span className="whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[9.5px] font-semibold text-primary">
              {t("включено {n} из {total}", { n: onChannels, total: channels.length })}
            </span>
          </div>
          <ul className="mt-2.5 flex flex-col divide-y divide-border/60">
            {channels.map((c) => (
              <li key={c.id} className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0">
                <span className="mt-0.5 grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-[13px] leading-none text-primary" aria-hidden>
                  <i className={c.icon} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11.5px] font-semibold leading-snug">{c.name}</p>
                  <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{c.description(email)}</p>
                </div>
                <Switch checked={c.on} onChange={() => toggleChannel(c.id)} label={`${c.name}: ${c.on ? t("включено") : t("выключено")}`} />
              </li>
            ))}
          </ul>
          {channels.find((c) => c.id === "browser")?.on ? (
            <p className="mt-2.5 flex items-start gap-1.5 rounded-btn bg-primary/[.07] px-2 py-1.5 text-[10.5px] leading-relaxed text-foreground/75">
              <i className="ri-checkbox-circle-line mt-[1px] text-[12px] leading-none text-primary" aria-hidden />
              {t("Разрешение браузера получено — уведомления появятся, даже когда вкладка свёрнута.")}
            </p>
          ) : (
            <p className="mt-2.5 flex items-start gap-1.5 rounded-btn bg-foreground/[.04] px-2 py-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
              <i className="ri-information-line mt-[1px] text-[12px] leading-none" aria-hidden />
              {t("Для всплывающих окон браузер спросит разрешение — его можно отозвать в настройках сайта.")}
            </p>
          )}
        </section>

        {/* о чём присылать */}
        <section className="rounded-btn border border-border bg-background/50 p-3.5">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-[12px] font-semibold">{t("О чём присылать")}</p>
            <span className="whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[9.5px] font-semibold text-primary">
              {t("включено {n} из {total}", { n: onEvents, total: events.length })}
            </span>
          </div>
          <ul className="mt-2.5 flex flex-col divide-y divide-border/60">
            {events.map((e) => (
              <li key={e.id} className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0">
                <span className="mt-0.5 grid size-7 flex-none place-items-center rounded-btn bg-foreground/[.05] text-[13px] leading-none text-foreground/70" aria-hidden>
                  <i className={e.icon} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11.5px] font-semibold leading-snug">{e.label}</p>
                  <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{e.description}</p>
                </div>
                <Switch checked={e.on} onChange={() => toggleEvent(e.id)} label={`${e.label}: ${e.on ? t("включено") : t("выключено")}`} />
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* частота и тихие часы */}
      <div className="mt-3 rounded-btn border border-border bg-background/50 p-3.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-[12px] font-semibold">{t("Как часто")}</p>
          <div className="flex gap-1 rounded-full bg-foreground/[.05] p-1">
            {notifyDigests.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, digest: d }))}
                aria-pressed={digest === d}
                className={cn(
                  "inline-flex h-6 cursor-pointer items-center whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold transition",
                  digest === d ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(d)}
              </button>
            ))}
          </div>
          <p className="text-[10.5px] leading-relaxed text-muted-foreground">
            {t("«Сразу» — по каждому событию, «Раз в час» — сборка, «Дайджест» — одно письмо утром.")}
          </p>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/60 pt-3">
          <div className="flex items-center gap-2">
            <Switch checked={quiet} onChange={() => setQuiet({ on: !quiet })} label={`${t("Тихие часы")}: ${quiet ? t("включены") : t("выключены")}`} />
            <p className="text-[12px] font-semibold">{t("Тихие часы")}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="time"
              value={q.from}
              onChange={(e) => setQuiet({ from: e.target.value })}
              disabled={!quiet}
              aria-label={t("Тихие часы с")}
              className={cn(TIME_INPUT, !quiet && "opacity-50")}
            />
            <span className="text-[11px] text-muted-foreground">—</span>
            <input
              type="time"
              value={q.to}
              onChange={(e) => setQuiet({ to: e.target.value })}
              disabled={!quiet}
              aria-label={t("Тихие часы до")}
              className={cn(TIME_INPUT, !quiet && "opacity-50")}
            />
          </div>
          <p className="text-[10.5px] leading-relaxed text-muted-foreground">
            {t("В это время всплывающие окна и письма не приходят — всё соберётся в одно письмо после тихих часов.")}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
        <button
          type="button"
          onClick={() => {
            const off = channels.filter((c) => !c.on);
            if (off.length === channels.length) {
              onFlash(t("Выберите хотя бы один способ доставки уведомлений"));
              return;
            }
            void save(prefs, t("Настройки уведомлений сохранены · событий включено {n}", { n: onEvents }));
          }}
          disabled={busy}
          className="inline-flex h-8 cursor-pointer disabled:opacity-60 items-center whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
        >
          {t("Сохранить")}
        </button>
        <button
          type="button"
          onClick={onTest}
          className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
        >
          <i className="ri-send-plane-line text-[13px] leading-none" aria-hidden />
          {t("Отправить тестовое")}
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
        >
          {t("Сбросить")}
        </button>
        <span className="inline-flex min-w-0 items-center gap-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
          <i className="ri-shield-check-line flex-none text-[12px] leading-none text-primary" aria-hidden />
          <span className="min-w-0">{t("Настройки касаются только вашего профиля; владелец пространства их не меняет.")}</span>
        </span>
      </div>
    </div>
  );
}